import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { workspaceParams } from '@playbooks/api';
import { useSession, useStorage, useStore } from '@playbooks/contexts';
import { useQuery } from '@playbooks/hooks';
import { logger } from '@playbooks/utils';

type WorkspaceType = 'personal' | 'collaborative' | '';

type WorkspaceContextValue = {
	type: WorkspaceType;
	workspace: any;
	setWorkspace: any;
	rootApi: string;
	rootLink: string;
	searchParams: any;
	loading: boolean;
	fetchInboxCount: any;
	fetchNotificationCount: any;
	onSwitch: any;
	onUpdate: any;
	onRefresh: any;
	loaded: boolean;
};

export const WorkspaceContext = React.createContext<WorkspaceContextValue | null>(null);

const WorkspaceProvider = ({ ssr, children }) => {
	const [workspace, setWorkspace] = useState<any>(() => {
		const workspaces = ssr.session?.data?.workspaces || [];
		return ssr.workspace?.data || workspaces.find(value => value.type === 'personal') || workspaces[0] || {};
	});
	const [type, setType] = useState<WorkspaceType>(workspace.type || 'personal');
	const [loaded, setLoaded] = useState(false);
	const router = useRouter();
	const session = useSession();
	const storage = useStorage();
	const store = useStore();

	// Computed
	const rootApi = `/workspace`;
	const rootLink = workspace.uuid ? `/${workspace.uuid}` : '';
	const searchParams = { 'owner.uuid': workspace.uuid };

	// Hooks
	useEffect(() => {
		if (!session.loaded) return;
		if (session.user.id) {
			if (ssr.workspace?.data?.id) {
				const resolvedType: WorkspaceType = ssr.workspace.data.type;
				setType(resolvedType);
				setWorkspace(ssr.workspace.data);
				storage.storeValues({ type: resolvedType, workspace: ssr.workspace.data });
				storage.storeCookies({ type: resolvedType, workspace: ssr.workspace.data.uuid });
				setLoaded(true);
				return;
			}
			const workspaces = session.user.workspaces || [];
			const storedWorkspace = workspaces.find(value => value.uuid === storage.storage.workspace?.uuid);
			const nextWorkspace =
				storedWorkspace || workspaces.find(value => value.type === 'personal') || workspaces[0] || {};
			const resolvedType: WorkspaceType = nextWorkspace.type || 'personal';
			setType(resolvedType);
			setWorkspace(nextWorkspace);
			storage.storeValues({ type: resolvedType, workspace: nextWorkspace });
			storage.storeCookies({ type: resolvedType, workspace: nextWorkspace.uuid });
		} else {
			setType('');
			setWorkspace({});
		}
		setLoaded(true);
	}, [session.loaded, session.user?.id, ssr.workspace?.data]);

	useEffect(() => {
		if (workspace.id && loaded) fetchData();
	}, [workspace.id, loaded, type]);

	useEffect(() => {
		if (!workspace.id || !loaded) return;
		fetchInboxCount();
		fetchNotificationCount();
	}, [workspace.id, loaded, type]);

	useEffect(() => {
		if (workspace.id) logger.debug('workspaceContext: ', { type, workspace });
	}, [workspace]);

	useEffect(() => {
		if (loaded && workspace.id && !workspace.access?.capabilities) {
			logger.error('workspaceContext: workspace access capabilities are missing', { type, workspaceId: workspace.id });
		}
	}, [loaded, type, workspace.id, workspace.access?.capabilities]);

	// Actions
	const [fetchData, loading, error] = useQuery(async () => {
		const response = await store.queryRecord({ url: rootApi, params: workspaceParams });
		setWorkspace(current => ({
			...response.data,
			access: response.data.access || current.access,
			totalInbox: current.totalInbox,
			totalNotifications: response.data.unreadNotifications ?? current.totalNotifications,
		}));
	});

	const [fetchNotificationCount, notificationsLoading] = useQuery(async () => {
		const response = await store.queryRecord({ url: rootApi + '/notifications/count' });
		setWorkspace(current => ({ ...current, totalNotifications: response.data.totalNotifications }));
	});

	const [fetchInboxCount, inboxLoading] = useQuery(async () => {
		const response = await store.queryRecord({ url: rootApi + '/inbox/count' });
		setWorkspace(current => ({ ...current, totalInbox: response.data.unread }));
	});

	// Methods
	const onSwitch = (nextType: WorkspaceType, nextWorkspace, reload = true) => {
		setType(nextType);
		setWorkspace(nextWorkspace);
		storage.storeValues({ type: nextType, workspace: nextWorkspace });
		storage.storeCookies({ type: nextType, workspace: nextWorkspace.uuid });
		if (reload) router.push(router.asPath);
	};

	const onUpdate = data => {
		const nextWorkspace = { ...workspace, ...data };
		setWorkspace(nextWorkspace);
		storage.storeValue('workspace', nextWorkspace);
		storage.storeCookie('workspace', nextWorkspace.uuid);
	};

	const onRefresh = () => {
		fetchData();
	};

	const value = useMemo<WorkspaceContextValue>(
		() => ({
			type,
			workspace,
			setWorkspace,
			rootApi,
			rootLink,
			searchParams,
			loading,
			fetchInboxCount,
			fetchNotificationCount,
			onSwitch,
			onUpdate,
			onRefresh,
			loaded,
		}),
		[type, workspace, loading, loaded],
	);

	// Render
	return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

const useWorkspace = () => {
	const context = React.useContext(WorkspaceContext);
	if (!context) throw new Error('useWorkspace must be used within a WorkspaceProvider.');
	return context;
};

export { WorkspaceProvider, useWorkspace };
export type { WorkspaceContextValue, WorkspaceType };
